/**
 * Success Score: how far an agent is set up and in use (8 steps). A hero with the animated ring, the level
 * and one segment per step; below, the open steps (most important first) with what each brings and a
 * direct action, then the completed ones, folded. The business description replaced the FAQ step
 * (Robert, 14.10.2026); the backend counts the same steps (platform/insights.py success_score).
 */
import { useEffect, useState } from 'react';
import { Modal, Box } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight, faChartLine, faCheck, faChevronDown, faComments, faFileAlt, faIdCard, faPalette, faSlidersH,
  faStar, faTimes, faTrophy, faUsers, faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './SuccessScoreModal.css';

const SUCCESS_CRITERIA = [
  { id: 'behavior', icon: faSlidersH, checkField: 'hasBehavior', importance: 'critical' },
  { id: 'paid', icon: faStar, checkField: 'hasPaidPlan', importance: 'critical' },
  { id: 'design', icon: faPalette, checkField: 'hasCustomDesign', importance: 'critical' },
  { id: 'business', icon: faIdCard, checkField: 'hasBusinessContext', importance: 'high' },
  { id: 'document', icon: faFileAlt, checkField: 'hasDocument', importance: 'high' },
  { id: 'questions', icon: faComments, checkField: 'hasEnoughQuestions', importance: 'high' },
  { id: 'leads', icon: faUsers, checkField: 'hasEnoughLeads', importance: 'high' },
  { id: 'reporting', icon: faChartLine, checkField: 'hasReporting', importance: 'medium' },
].map((c) => ({
  ...c,
  titleKey: `success_${c.id}_title`,
  subtitleKey: `success_${c.id}_subtitle`,
  descriptionKey: `success_${c.id}_description`,
  actionKey: `success_${c.id}_action`,
}));

// Calculate score data from bot
export const calculateSuccessScore = (bot, successData = {}) => {
  const checks = {
    hasEnoughQuestions: (successData.totalQuestions ?? 0) >= 50,
    hasEnoughLeads: (successData.totalLeads ?? 0) >= 3,
    hasCustomDesign: true, // Always true for now
    hasDocument: successData.hasDocument ?? false,
    hasBusinessContext: successData.hasBusinessContext ?? false,
    hasBehavior: successData.hasBehavior ?? false,
    hasPaidPlan: bot?.subscription_plan?.name !== 'Free',
    hasReporting: successData.hasReporting ?? false,
  };

  const completed = Object.values(checks).filter(Boolean).length;
  const total = Object.keys(checks).length;
  const percentage = Math.round((completed / total) * 100);

  return { checks, completed, total, percentage };
};

// Get color class based on score
export const getScoreColorClass = (percentage) => {
  if (percentage <= 25) return 'score-low';
  if (percentage <= 40) return 'score-medium-low';
  if (percentage <= 60) return 'score-medium';
  if (percentage <= 80) return 'score-medium-high';
  return 'score-high';
};

const levelOf = (pct) => (pct >= 100 ? 'done' : pct >= 65 ? 'almost' : pct >= 35 ? 'progress' : 'start');

// ids of the steps still open, most important first (Mo's welcome in the AI Studio names them)
export const openSuccessSteps = (bot, successData = {}) => {
  const { checks } = calculateSuccessScore(bot, successData);
  return SUCCESS_CRITERIA.filter((c) => !checks[c.checkField]).map((c) => c.id);
};

function useCountUp(target, ms = 900) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) { setValue(target); return undefined; }
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const f = Math.min(1, (now - t0) / ms);
      setValue(Math.round(target * (1 - (1 - f) ** 3)));
      if (f < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return value;
}

function ScoreRing({ percentage }) {
  const r = 46;
  const c = 2 * Math.PI * r;
  const shown = useCountUp(percentage);
  const [drawn, setDrawn] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setDrawn(percentage));
    return () => cancelAnimationFrame(id);
  }, [percentage]);
  return (
    <div className="ssm-ring" role="img" aria-label={`${percentage}%`}>
      <svg viewBox="0 0 112 112" aria-hidden="true">
        <defs>
          <linearGradient id="ssmRing" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#d9ccff" />
          </linearGradient>
        </defs>
        <circle cx="56" cy="56" r={r} className="ssm-ring-track" />
        <circle cx="56" cy="56" r={r} className="ssm-ring-value" strokeDasharray={c}
          strokeDashoffset={c * (1 - drawn / 100)} transform="rotate(-90 56 56)" />
      </svg>
      <span className="ssm-ring-text">{shown}<small>%</small></span>
    </div>
  );
}

// steps the AI Studio's side panel takes care of (onOpenPanel): its tab
const PANEL_TABS = { behavior: 'behavior', business: 'profile', leads: 'skills' };

// onHelpFix (optional, AI Studio): "Help me fix it" hands the open steps to Mo; called with their titles.
// onOpenPanel (optional, AI Studio): the actions for behavior, business description and leads open its panel tab.
const SuccessScoreModal = ({ open, onClose, bot, successData, onSettingsClick, onUpgradeClick, onHelpFix, onOpenPanel }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDone, setShowDone] = useState(false);
  const { checks, completed, total, percentage } = calculateSuccessScore(bot, successData);
  const todo = SUCCESS_CRITERIA.filter((c) => !checks[c.checkField]);
  const done = SUCCESS_CRITERIA.filter((c) => checks[c.checkField]);
  const level = levelOf(percentage);

  const handleAction = (criteriaId) => {
    onClose();
    if (onOpenPanel && PANEL_TABS[criteriaId]) {
      onOpenPanel(PANEL_TABS[criteriaId]);
      return;
    }
    // Set the bot as selected before navigating
    localStorage.setItem('selectedBotId', bot.id);
    localStorage.setItem('selectedBotIds', JSON.stringify([bot.id]));
    switch (criteriaId) {
      case 'questions':
        navigate(`/integration?bot_id=${bot.id}`); // more traffic: put the chat on more pages and channels
        break;
      case 'leads':
        localStorage.setItem('settingsTab', 'leads');
        onSettingsClick?.(bot, 'leads');
        break;
      case 'design':
        localStorage.setItem('settingsTab', 'design');
        onSettingsClick?.(bot, 'design');
        break;
      case 'document':
        localStorage.setItem('knowledgeActiveTab', 'documents');
        localStorage.setItem('settingsTab', 'knowledge');
        onSettingsClick?.(bot, 'knowledge');
        break;
      case 'business':
        navigate('/knowledge/business-context');
        break;
      case 'behavior':
        localStorage.setItem('settingsTab', 'behavior');
        onSettingsClick?.(bot, 'behavior');
        break;
      case 'paid':
        onUpgradeClick?.(bot);
        break;
      case 'reporting':
        navigate('/analytics?tab=reporting');
        break;
      default:
        break;
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="ssm-overlay" onClick={onClose}>
        <div className="ssm" role="dialog" aria-labelledby="ssm-title" onClick={(e) => e.stopPropagation()}>
          <header className={`ssm-hero is-${level}`}>
            <button type="button" className="ssm-close" onClick={onClose} aria-label={t('close')}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <ScoreRing percentage={percentage} />
            <div className="ssm-hero-text">
              <span className="ssm-eyebrow"><FontAwesomeIcon icon={faTrophy} /> {t('success_score_title')}</span>
              <h2 id="ssm-title">{t(`success_level_${level}`)}</h2>
              <p>{t('success_score_summary', { completed, total })} · {t(`success_level_${level}_hint`)}</p>
              <div className="ssm-segments" aria-hidden="true">
                {SUCCESS_CRITERIA.map((c, i) => (
                  <span key={c.id} className={checks[c.checkField] ? 'is-done' : ''} style={{ '--i': i }}
                    title={t(c.titleKey)} />
                ))}
              </div>
              {onHelpFix && todo.length > 0 && (
                <button type="button" className="ssm-fix" onClick={() => onHelpFix(todo.map((c) => t(c.titleKey)))}>
                  <FontAwesomeIcon icon={faWandMagicSparkles} /> {t('agent_studio_score_help_fix')}
                </button>
              )}
            </div>
          </header>

          <div className="ssm-body">
            {todo.length > 0 && (
              <section className="ssm-group">
                <h3 className="ssm-group-title">{t('success_group_open', { count: todo.length })}</h3>
                {todo.map((c, i) => (
                  <div key={c.id} className="ssm-step" style={{ '--i': i }}>
                    <span className="ssm-step-icon"><FontAwesomeIcon icon={c.icon} /></span>
                    <div className="ssm-step-main">
                      <div className="ssm-step-head">
                        <span className="ssm-step-title">{t(c.titleKey)}</span>
                        <span className={`ssm-pill is-${c.importance}`}>{t(`success_importance_${c.importance}`)}</span>
                      </div>
                      <p className="ssm-step-text">{t(c.descriptionKey)}</p>
                    </div>
                    <button type="button" className="ssm-step-action" onClick={() => handleAction(c.id)}>
                      {t(c.actionKey)} <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                  </div>
                ))}
              </section>
            )}
            {todo.length === 0 && (
              <div className="ssm-all-done">
                <span className="ssm-all-done-icon"><FontAwesomeIcon icon={faTrophy} /></span>
                <p>{t('success_level_done_text')}</p>
              </div>
            )}
            {done.length > 0 && (
              <section className="ssm-group">
                <button type="button" className={`ssm-group-toggle ${showDone ? 'is-open' : ''}`} aria-expanded={showDone}
                  onClick={() => setShowDone((v) => !v)}>
                  <span className="ssm-group-title">{t('success_group_done', { count: done.length })}</span>
                  <FontAwesomeIcon icon={faChevronDown} />
                </button>
                {showDone && done.map((c) => (
                  <button type="button" key={c.id} className="ssm-done" onClick={() => handleAction(c.id)}>
                    <span className="ssm-done-check"><FontAwesomeIcon icon={faCheck} /></span>
                    <span className="ssm-done-title">{t(c.titleKey)}</span>
                    <span className="ssm-done-sub">{t(c.subtitleKey)}</span>
                    <FontAwesomeIcon icon={faArrowRight} className="ssm-done-go" />
                  </button>
                ))}
              </section>
            )}
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default SuccessScoreModal;
